import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Trash2, AlertTriangle, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../../reducers';
import { folderActions } from '../../librarySlice';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../../components/ui/dialog';
import { Alert, AlertDescription } from '../../../../components/ui/alert';
import { Button } from '../../../../components/ui/button';
import type { FolderType } from '../../../../types';
import { RouteParams } from '../../types';
import { getTeamUrl } from '../../../../lib/routingUtils';

interface DeleteFolderModalProps {
  onClose: () => void;
}

const DeleteFolderModal: React.FC<DeleteFolderModalProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { teamId, folderId: routeFolerId } = useParams<RouteParams>();
  const navigate = useNavigate();
  const { folder, status } = useSelector((state: RootState) => ({
    folder: state.globalModals.actionData.files?.[0] as FolderType,
    status: state.library.folders.status,
  }));
  const { _id: folderId, name } = folder;
  const isLoading = status === 'pending';

  const handleConfirm = () => {
    dispatch(folderActions({ folderId, action: 'delete' }));
    onClose();
    if (routeFolerId === folderId && teamId && folderId) {
      navigate(getTeamUrl({ teamId }));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            {t('library.folder.delete.title', {
              name,
              defaultValue: `Delete "${name}"`,
            })}
          </DialogTitle>
          <DialogDescription>
            {t(
              'library.folder.delete.description',
              'If you proceed, you will lose access to all files and subfolders within this folder. Make sure you have backed up any important content before continuing.',
            )}
          </DialogDescription>
        </DialogHeader>

        <Alert variant="destructive" className="my-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {t(
              'library.folder.delete.warning',
              'This action cannot be undone. All contents within this folder will be permanently deleted.',
            )}
          </AlertDescription>
        </Alert>

        <DialogFooter>
          <Button
            variant="outline"
            disabled={isLoading}
            onClick={onClose}
            data-testid="cancel-delete-folder-button"
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isLoading}
            data-testid="confirm-delete-folder-button"
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <Loader className="h-4 w-4 animate-spin text-current" />
                {t('library.folder.delete.deleting', 'Deleting...')}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Trash2 className="h-4 w-4" />
                {t('library.folder.delete.deleteFolder', 'Delete Folder')}
              </div>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteFolderModal;
